import React, { useState } from "react";
import ProductionPlanner from "@/pages/ProductionPlanner";
import BrandManager from "@/pages/BrandManager";
import BrandAdmin from "@/pages/BrandAdmin";
import BrandAssetGallery from "@/pages/BrandAssetGallery";
import { Kanban, BookOpen, Palette, Image as ImageIcon, Wand2 } from "lucide-react";

/**
 * AmplifyPage — shell for every outward-facing brand/content surface.
 *
 * IA fix (May 2026): Brand Assets and Brand Kit used to live in the
 * Settings drawer because they're admin-only. Wrong axis — admin-only
 * is about who can see them, not what kind of work they are. Both are
 * inputs to Amplify (you tune the brand kit + curate the asset pool,
 * then make content). They now live as Amplify tabs so the brand-content
 * workflow is one section of the app, not three scattered sidebar entries.
 *
 * Note: the legacy "Brand" tab → BrandManager is older brand-config
 * UI that overlaps with Brand Kit's Palette/Voice tabs. Kept here for
 * now so existing operators don't lose surfaces they're used to;
 * consolidation deferred until Remi confirms which surface he actually
 * uses day-to-day.
 */
const TABS = [
  { key: "planner", label: "Planner", icon: Kanban, comp: ProductionPlanner },
  { key: "library", label: "Library", icon: BookOpen, comp: null },
  { key: "brand-assets", label: "Brand Assets", icon: ImageIcon, comp: BrandAssetGallery, adminOnly: true },
  { key: "brand-kit", label: "Brand Kit", icon: Wand2, comp: BrandAdmin, adminOnly: true },
  { key: "brand", label: "Brand (legacy)", icon: Palette, comp: BrandManager, adminOnly: true },
];

export default function AmplifyPage({ user }) {
  const [tab, setTab] = useState("planner");
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  const visibleTabs = TABS.filter((t) => !t.adminOnly || isAdmin);
  const Active = visibleTabs.find((t) => t.key === tab) || visibleTabs[0];
  const Comp = Active?.comp;

  return (
    <div className="flex flex-col h-full" data-testid="amplify-page">
      <div className="flex items-baseline justify-between px-6 pt-4">
        <h1 className="text-2xl font-bold text-slate-900">Amplify</h1>
        <span className="text-xs text-slate-500">What makes Revenue numbers easier next quarter</span>
      </div>
      <div className="flex gap-2 border-b border-slate-200 px-6 overflow-x-auto">
        {visibleTabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`amplify-tab-${t.key}`}
              className={`flex items-center gap-2 px-3 py-2 text-sm border-b-2 transition-colors whitespace-nowrap ${
                active
                  ? "border-blue-600 text-blue-700 font-semibold"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              <Icon className="h-4 w-4" />
              {t.label}
            </button>
          );
        })}
      </div>
      <div className="flex-1 overflow-auto" data-testid={`amplify-pane-${tab}`}>
        {Comp ? (
          <Comp user={user} />
        ) : (
          <div className="p-8 text-sm text-slate-500">
            Library tab coming soon — published-content archive,
            filterable by date/voice/market/platform/performance.
          </div>
        )}
      </div>
    </div>
  );
}
